/**
 * The conversation's attached Investigator result.
 *
 * `state` is the whole point of these calls and must never be collapsed into
 * "no results". A stale, unauthorized or vanished reference has to read as
 * UNAVAILABLE — rendering it as an empty run is the defect this subsystem
 * exists to prevent (`document_ids=[]`, `held_results=0`, and now a poisoned
 * attachment that would persist across every turn instead of dying with one
 * request).
 */
import { auth } from '../config/firebase';
import { getImpersonationHeaders } from './impersonationHeaders';
import { parseJobProgress, type JobProgress } from './watchedJobs';

const API_BASE_URL = import.meta.env.VITE_API_URL ||
  'https://us-central1-label-doctor-f255e.cloudfunctions.net/apiProxy';

/** Mirrors `cepi_reference_state`. Only two of these are usable. */
export type ReferenceState =
  | 'active' | 'empty' | 'stale' | 'unauthorized' | 'missing' | 'failed';

export const USABLE_STATES: ReferenceState[] = ['active', 'empty'];

export interface ActiveResultCoverage {
  total: number;
  analysed: number;
  matched: number;
  not_matched: number;
  complete: boolean;
  job_status: string;
}

export interface ActiveResult {
  state: ReferenceState;
  job_id?: string | null;
  criterion?: string | null;
  /** Investigator only — per-member verdict coverage. */
  coverage?: ActiveResultCoverage | null;
  /** Which capability produced it. Absent on rows written before this field. */
  capability?: 'investigator' | 'cogni' | 'keyword' | null;
  /** Rows in a captured Cogni / Keyword set. */
  rows?: number | null;
  /**
   * Whether an attached KEYWORD census is complete enough to support "so
   * nothing says it". Always false for Cogni — no ranked search is a census.
   */
  proves_absence?: boolean | null;
}

async function headers(): Promise<Record<string, string>> {
  const user = auth.currentUser;
  const token = user ? await user.getIdToken(false) : null;
  return {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` }),
    ...getImpersonationHeaders(),
  };
}

/** Bind a run to a conversation. Returns the server-minted thread id. */
export async function attachResult(
  jobId: string,
  opts: { threadId?: string | null | undefined; mode?: 'auto' | 'user' } = {},
): Promise<{ threadId?: string; attached: boolean; reason?: string }> {
  try {
    const res = await fetch(`${API_BASE_URL}/v2/research/${jobId}/attach`, {
      method: 'POST',
      headers: await headers(),
      body: JSON.stringify({
        thread_id: opts.threadId ?? null,
        mode: opts.mode ?? 'user',
      }),
    });
    if (!res.ok) return { attached: false };
    const body = await res.json();
    return {
      threadId: typeof body?.thread_id === 'string' ? body.thread_id : undefined,
      attached: body?.attached === true,
      reason: typeof body?.reason === 'string' ? body.reason : undefined,
    };
  } catch {
    return { attached: false };
  }
}

export async function detachActiveResult(threadId: string): Promise<boolean> {
  try {
    const res = await fetch(
      `${API_BASE_URL}/v2/research/threads/${threadId}/detach`,
      { method: 'POST', headers: await headers() },
    );
    if (!res.ok) return false;
    return (await res.json())?.detached === true;
  } catch {
    return false;
  }
}

/**
 * What the chip renders. Returns null only when the request itself failed —
 * which is NOT the same as "nothing attached" (that comes back as a `missing`
 * state), so the caller can tell a network problem from an empty bench.
 */
export async function fetchActiveResult(
  threadId: string,
): Promise<ActiveResult | null> {
  try {
    const res = await fetch(
      `${API_BASE_URL}/v2/research/threads/${threadId}/active`,
      { headers: await headers() },
    );
    if (!res.ok) return null;
    const body = await res.json();
    return typeof body?.state === 'string' ? (body as ActiveResult) : null;
  } catch {
    return null;
  }
}

export function isUsable(state: ReferenceState | undefined): boolean {
  return !!state && USABLE_STATES.includes(state);
}

/**
 * A run's live state, for the completion poll AND the progress display.
 *
 * Returns null on any failure — indistinguishable from "still running" on
 * purpose, because the caller simply polls again; a transient network blip
 * must not abandon the poll.
 *
 * This used to return `status` alone and discard the rest of the payload,
 * which is how a healthy run spent ten minutes under the word "Starting…".
 * The response already carries the phase message and the counters; the poll
 * that fetches them is the same one. Returning the object costs nothing and
 * survives the next field the backend adds.
 */
export async function fetchJobProgress(jobId: string): Promise<JobProgress | null> {
  try {
    const res = await fetch(`${API_BASE_URL}/v2/research/${jobId}`, {
      headers: await headers(),
    });
    if (!res.ok) return null;
    return parseJobProgress(await res.json());
  } catch {
    return null;
  }
}

/**
 * The caller's in-flight research jobs (last 24h). Unlike the other pollers
 * here this THROWS on failure: the caller must tell "couldn't load" from "none".
 */
export async function fetchActiveResearchJobs(): Promise<unknown[]> {
  const res = await fetch(`${API_BASE_URL}/v2/research?active=true&limit=20`, {
    headers: await headers(),
  });
  if (!res.ok) throw new Error(`active research jobs: HTTP ${res.status}`);
  return (await res.json()) as unknown[];
}

export type CancelResult = 'cancelled' | 'ended' | 'error';

/** Stop a run: 200 cancelled, 409 already ended, anything else (or a throw) error. Never DELETE. */
export async function cancelResearchJob(jobId: string): Promise<CancelResult> {
  try {
    const res = await fetch(`${API_BASE_URL}/v2/research/${jobId}/cancel`, {
      method: 'POST',
      headers: await headers(),
    });
    if (res.ok) return 'cancelled';
    return res.status === 409 ? 'ended' : 'error';
  } catch {
    return 'error';
  }
}
